import { makeObservable, observable, reaction } from 'mobx';
import { getRoot } from 'mobx-easy';
import { ApplicationUuid, Task } from '@bringg/types';
import { TimelineItem, TimelineRow } from '@bringg-frontend/bringg-timeline';
import { GanttPublicApi } from '@bringg-frontend/gantt-timeline';
import { getTaskStatusClass, taskStyles } from '@bringg-frontend/monitoring';

import { getActionableWaypoint } from 'bringg-web/stores/tasks/task-utils';
import RootStore from 'bringg-web/stores/root-store';
import { routesMonitoringRootStore } from './routes-monitoring-root-store';
import RoutesMonitoringStore from './routes-monitoring-store';
import { timelineProvider } from '../tasks-timeline/timeline-provider';

class RoutesMonitoringTimelineViewStore {
	rows: TimelineRow[] = [];
	isRealtimeEtaEnabled: boolean;
	routesMonitoringStore: RoutesMonitoringStore;
	zoom: { start?: number; end?: number };
	shouldZoom: boolean;

	_lastScrollPosition?: number;
	_timelineApi?: GanttPublicApi;

	constructor(disposeSignal: AbortSignal) {
		makeObservable(this, {
			zoom: observable,
			rows: observable
		});

		this.routesMonitoringStore = routesMonitoringRootStore.getStore().routesMonitoringStore;

		const { merchantConfigurationsStore } = getRoot<RootStore>().data;
		this.isRealtimeEtaEnabled = !!merchantConfigurationsStore.findApplication(ApplicationUuid.RealtimeEtaApp);

		reaction(
			() => [this.routesMonitoringStore.loading, this.routesMonitoringStore.refreshing],
			() => {
				if (!this.routesMonitoringStore.loading && !this.routesMonitoringStore.refreshing) {
					this.updateRows();
				}
			},
			{ signal: disposeSignal }
		);
	}

	getRowItems = (tasks: Task[]) => {
		let startEnd = { start: 0, end: 0 };

		const mappedItems: TimelineItem[] = tasks.map((task: Task) => {
			const wayPoint = getActionableWaypoint(task);
			const etos = wayPoint.etos ? wayPoint.etos / 60 : 2;

			const itemClass = getTaskStatusClass(task, this.isRealtimeEtaEnabled);

			const startTime = wayPoint.checkin_time || wayPoint.eta || wayPoint.no_earlier_than;

			const startDate = startTime ? new Date(startTime) : new Date();
			let endTime = startTime ? new Date(startTime) : new Date();

			if (wayPoint.checkout_time) {
				endTime = new Date(wayPoint.checkout_time);
			} else {
				endTime.setMinutes(endTime.getMinutes() + etos);
			}

			startEnd = {
				start: !startEnd.start || startDate.getTime() < startEnd.start ? startDate.getTime() : startEnd.start,
				end: endTime.getTime() > startEnd.end ? endTime.getTime() : startEnd.end
			};

			return {
				id: task.id,
				rowId: task.run_id,
				className: itemClass,
				selectedClassName: taskStyles.selectedTask,
				start: startDate.valueOf(),
				end: endTime.valueOf()
			};
		});

		return { items: mappedItems, startEnd };
	};

	updateRows = () => {
		const runs = this.routesMonitoringStore.detailedRuns;
		const rows = new Array(runs.length);

		const startEnd = { start: 0, end: 0 };

		for (let index = 0; index < runs.length; index++) {
			const run = runs[index];
			const { id } = run;

			const { items, startEnd: rowStartEnd } = this.getRowItems([
				...(this.routesMonitoringStore.tasksByRun.get(id)?.values() || [])
			]);

			startEnd.start = !startEnd.start || rowStartEnd.start < startEnd.start ? rowStartEnd.start : startEnd.start;
			startEnd.end = rowStartEnd.end > startEnd.end ? rowStartEnd.end : startEnd.end;

			const range = timelineProvider.getRangeItem(run);

			range && items.unshift(range);

			rows[index] = {
				rowId: +id,
				hasError: this.routesMonitoringStore.isRouteInvalid(id),
				order: index,
				items
			};
		}

		if (startEnd.start > 0) {
			this.zoom = { ...timelineProvider.addMarginToZoom(startEnd) };
		}

		this.rows = rows;
	};
}

export default RoutesMonitoringTimelineViewStore;
